import type { MonetizationSettings } from "./settings";

/**
 * `DEFAULT_SETTINGS` lives in its own module, separate from `settings.ts`
 * (which imports `zod` to define/validate the schema), so client bundles that
 * only need the plain-object floor — never the validator — don't pull zod in.
 * Measured 2026-09-26: zod was a ~61 KB gz chunk shipped to every page with an
 * ad slot (`use-ad-guardrails.ts` only needs these defaults, not the schema).
 * `settings.ts` re-exports this for every existing server-side import.
 */

/** The always-safe floor. */
export const DEFAULT_SETTINGS: MonetizationSettings = {
	forceProvider: null, // normal rotation; pin a provider via the PostHog payload
	engine: {
		// Master switch. The live `monetization-settings` payload sets this TRUE
		// (verified 2026-09-02: /api/monetization/offer returns a ranked provider
		// with decision.reason "ctr"); this code default is the safe floor, not
		// the production state.
		//
		// Flipping it false is now a TOTAL monetization kill: since the legacy
		// cards were deleted, every affiliate surface renders an empty reserved
		// box rather than handing over to a second, unlabelled card. That is
		// intended — a kill switch that quietly keeps serving ads is not one. To
		// silence a single placement, use `surfaces.<name>.enabled` below.
		enabled: false, // master off until rollout; flip via PostHog
		minImpressionsForCtr: 200,
		banditThresholdConvPerArm: 150,
		explorationRate: 0.3, // 30% fair-exposure exploration — at current low volume 0.1 left the long-tail arms (educative/shopee/contra) statistically invisible; 0.3 gives them real exposure to learn CTR. Tune in PostHog; lower once CTRs stabilize.
		ctrPrior: { alpha: 1, beta: 99 }, // ~1% prior CTR
		holdoutPct: 0,
		contextualRelevance: false, // ships dark; flip in PostHog to activate
		// Self-improving ad-copy loop (Wave 4b) — ships DARK. See the schema doc
		// in settings.ts for what each key does and the rewardSince rationale.
		copyGeneration: {
			enabled: false,
			variantsPerRun: 2,
			maxVariantsPerOffer: 4,
			explorationRate: 0.15,
			minVariantImpressions: 200,
			pruneBelow: 0.1,
			model: "@cf/meta/llama-3.2-3b-instruct",
			locales: ["id", "en"],
			rewardSince: 1790560560000, // 2026-09-28T01:56:00Z — PR #5897 /go bot-filter landed
		},
		// Contextual ad targeting (Wave 4c) — ships DARK. See the schema doc in
		// settings.ts; the payload `engine.targeting` block is the sole switch.
		targeting: {
			enabled: false,
			holdoutPct: 10,
			minImpressions: 150,
			fatigueImpressions: 4,
			fatigueDecay: 0.3,
			cooldownHours: 24,
			lockThreshold: 0.9,
			lockedExploration: 0.05,
			// Literal (not imported): this module must stay import-type-only so
			// client bundles never pull a runtime dependency through it. Same
			// order as TARGETING_DIMENSIONS; the last dimension backs off first.
			dimensions: [
				"device",
				"traffic",
				"funnel",
				"page_family",
				"market",
				"segment",
			],
			priorStrength: 50,
			statsMaxAgeHours: 3,
		},
	},
	// ROUGH per-click ($) estimates — tune in PostHog with real partner data.
	//
	// declaredValue is EPC (expected revenue PER CLICK, USD), NOT click-through
	// rate. The ranker computes eEPM = pClick × declaredValue × 1000; while an arm
	// is below `minImpressionsForCtr` its pClick falls back to the shared 1% prior,
	// so below threshold the ONLY differentiator is declaredValue. We still have no
	// measured per-click PAYOUT in PostHog (affiliate revenue is reconciled on the
	// partner dashboards, not captured here), so these stay estimates — DO NOT treat
	// them as measured EPC. The live switch is the PostHog `monetization-settings`
	// payload; seed it the moment partner-dashboard EPC lands.
	//
	// 2026-06-20 measured-CTR nudge, REVERSED 2026-07-04: wise had the highest 90d
	// CTR (9.95%, 21/211) of any arm, so its prior was nudged 0.15 → 0.30 to stop
	// under-ranking it. But CTR is a click-through proxy, not confirmed revenue —
	// those clicks never reconciled to actual payout on the Wise partner dashboard.
	// Deprioritized back down to 0.1 per direct instruction: don't let a
	// high-CTR/no-cash arm outrank the confirmed earners (shopee/educative/deel).
	// Other arms left as-is — their CTRs are statistically indistinguishable from
	// each other and the prior, and there is no EPC signal to separate them.
	// No overrides by default — code URLs are the floor. Populate via the
	// PostHog payload, e.g. `{"links":{"wise":"https://wise.com/invite/..."}}`.
	links: {},
	payouts: {
		educative: {
			payoutModel: "cps",
			declaredValue: 0.4,
			enabled: true,
			market: "all",
			// Paid courses bought by the person taking the interview. An employer
			// browsing /employers/post cannot convert on "Ace the technical
			// interview".
			audience: "candidate",
			categoryAffinity: ["engineering", "ai", "data-analytics", "product"],
		},
		deel: {
			payoutModel: "cps",
			declaredValue: 0.5,
			enabled: true,
			market: "all",
			// Deel pays when a COMPANY signs up for EOR/contractor payroll. A job
			// seeker cannot buy it, which is why 5,775 seeker-side impressions in
			// 30 days produced 17 clicks and zero conversions. Employer surfaces
			// only. The contractual blog CTA is unaffected: it renders through
			// <PinnedOffer>, which bypasses the ranker entirely.
			audience: "employer",
		},
		remote: {
			// Remote.com EOR partner (NDA 2026-08-18, rev-share terms still being
			// finalized — payout per signup unknown). Same employer-only reasoning
			// as deel. declaredValue mirrors deel's 0.5 so the two EOR arms share
			// the employer slots under Thompson sampling instead of one starving the
			// other on a prior nobody has measured; the deterministic tie-break
			// (partner name) still resolves to deel. Tune in the PostHog payload
			// once either arm has real conversion data.
			payoutModel: "cps",
			declaredValue: 0.5,
			enabled: true,
			market: "all",
			audience: "employer",
		},
		visualcv: {
			// Resume builder (PartnerStack, 20% recurring). Category-agnostic — every
			// seeker needs a resume — so no categoryAffinity. ROUGH per-click EPC
			// estimate; kept below deel/educative so it doesn't dominate the prior
			// while CTR is untrusted. Serves nothing until AFFILIATE_VISUALCV_URL is
			// set post-approval (the provider returns null). Tune in the PostHog
			// payload once partner-dashboard EPC lands.
			payoutModel: "cps",
			declaredValue: 0.3,
			enabled: true,
			market: "all",
			// A resume builder sells to the applicant, never to the company
			// reading the application.
			audience: "candidate",
		},
		wise: {
			payoutModel: "cpl",
			// 2026-07-04: deprioritized 0.30 → 0.1 per direct instruction — high
			// measured CTR (9.95%, 90d) never converted to confirmed payout, so the
			// declaredValue prior was overstating it. Not disabled (still eligible
			// during exploration via ε=0.3), just no longer allowed to prior-rank
			// above the arms with real confirmed revenue (shopee/educative/deel).
			declaredValue: 0.1,
			enabled: true,
			market: "all",
			// "Get paid in USD — keep more of it": a personal multi-currency
			// account for the person receiving the salary. `AffiliateConfig.wise`
			// in src/lib/affiliate/links.ts has said `candidate` since it was
			// written; this is the ranker finally reading it.
			audience: "candidate",
		},
		shopee: {
			// PREFERRED ARM (Indonesia). Shopee is the best-converting real earner for
			// the ID audience (7-day full-basket attribution), so its declaredValue
			// leads the ID pack: prior eEPM ranks it first while CTR is still untrusted.
			// It is NOT pinned — every other arm stays `enabled`, so Thompson sampling
			// + ε-greedy on the request path (`sample: true`) keep exploring them and a
			// measured-CTR winner can overtake Shopee. This is a weighted preference,
			// not a force. Tune the value in the PostHog `monetization-settings` payload
			// as real EPC data lands.
			payoutModel: "cps",
			declaredValue: 0.6,
			enabled: true,
			market: "indonesia",
			// "Remote-work gear at Shopee prices" — a consumer basket for someone
			// setting themselves up to work remotely. It won an employer surface 9
			// times in 12 on 2026-09-13; a company hiring through /employers/post
			// is not the buyer.
			audience: "candidate",
		},
		global_deal: {
			// 2026-08-04: disabled by code default (PR-2, freemium-monetization
			// remediation) — `GLOBAL_PRODUCTS` urls in global-products.ts are still
			// clean/unmonetized direct links (no affiliate program enrolled), so this
			// arm was burning global-market impressions for $0. Re-enable per-partner
			// via the PostHog `monetization-settings` payload (no deploy needed) once
			// real affiliate/referral tracking URLs exist.
			payoutModel: "cps",
			declaredValue: 0.1,
			enabled: false,
			market: "global",
			// GLOBAL_PRODUCTS is upskilling + job-search tooling for the global
			// seeker (see global-products.ts).
			audience: "candidate",
		},
		contra: {
			payoutModel: "cpl",
			declaredValue: 0.08,
			enabled: true,
			market: "all",
			// "Find remote clients on Contra" — the offer is the freelancer side of
			// the marketplace. Contra does have a client side, but this arm's copy
			// sells the worker side and the referral credits a freelancer signup.
			audience: "candidate",
		},
		allo_paylater: {
			// Allo PayLater BNPL (Accesstrade, CPA ~Rp13,500/qualified action ≈
			// $0.85). 2026-07-04: prioritized 0.15 → 0.55 per direct instruction —
			// ranks alongside shopee/educative. Now LIVE: public tracked link
			// hardcoded in link-partners.ts (Accesstrade "Allo PayLater CPA" AMBIL
			// LINK), so it serves unconditionally — no secret required.
			payoutModel: "cps",
			declaredValue: 0.55,
			enabled: true,
			market: "indonesia",
			// "Manage cashflow while job-hunting" — consumer BNPL for a person
			// between jobs.
			audience: "candidate",
		},
		fiverr: {
			// Fiverr in-house affiliate ($10 CPA + 10% rev-share). Per-click EPC
			// estimate, kept below deel/educative so it doesn't dominate. Global+ID.
			// Serves nothing until AFFILIATE_URL_FIVERR is set. Tune in PostHog.
			// 2026-08-04: disabled by code default (PR-2) — zero clicks over 90d
			// telemetry. Re-enable via the PostHog payload once a tracking URL lands.
			//
			// Deliberately left undeclared (= "both"): the affiliate pays when a
			// BUYER purchases, and the copy sells both sides ("sell your services or
			// hire talent"). A company hiring through /employers/post is a plausible
			// Fiverr buyer, so this arm genuinely converts either audience.
			payoutModel: "cps",
			declaredValue: 0.25,
			enabled: false,
			market: "all",
		},
		nordvpn: {
			// NordVPN in-house affiliate (up to 100% first sale, 30% recurring).
			// Recurring tail → slightly above fiverr but still below deel. Global+ID.
			// Serves nothing until AFFILIATE_URL_NORDVPN is set. Tune in PostHog.
			// 2026-08-04: disabled by code default (PR-2) — zero clicks over 90d
			// telemetry. Re-enable via the PostHog payload once a tracking URL lands.
			//
			// Deliberately left undeclared (= "both"): a VPN subscription converts a
			// remote worker and a hiring company equally well.
			payoutModel: "cps",
			declaredValue: 0.3,
			enabled: false,
			market: "all",
		},
		tokopedia: {
			// Tokopedia CPS (Accesstrade, up to 24%). ID marketplace. Per-click EPC
			// estimate below shopee (0.6) so it never dominates the ID pack while CTR
			// is untrusted. Serves nothing until AFFILIATE_URL_TOKOPEDIA is set.
			// 2026-08-04: disabled by code default (PR-2) — zero clicks over 90d
			// telemetry. Re-enable via the PostHog payload once a tracking URL lands.
			payoutModel: "cps",
			declaredValue: 0.2,
			enabled: false,
			market: "indonesia",
			// Consumer marketplace basket — the same buyer as shopee.
			audience: "candidate",
		},
		tiktok_shop: {
			// TikTok Shop ID (Accesstrade, up to 25%). ID marketplace. Kept below
			// shopee. Serves nothing until AFFILIATE_URL_TIKTOK_SHOP is set.
			// 2026-08-04: disabled by code default (PR-2) — zero clicks over 90d
			// telemetry. Re-enable via the PostHog payload once a tracking URL lands.
			payoutModel: "cps",
			declaredValue: 0.2,
			enabled: false,
			market: "indonesia",
			// Consumer marketplace basket — the same buyer as shopee.
			audience: "candidate",
		},
		blibli: {
			// Blibli CPS (Accesstrade). ID marketplace, electronics-heavy. Kept below
			// shopee. Serves nothing until AFFILIATE_URL_BLIBLI is set.
			// 2026-08-04: disabled by code default (PR-2) — zero clicks over 90d
			// telemetry. Re-enable via the PostHog payload once a tracking URL lands.
			payoutModel: "cps",
			declaredValue: 0.15,
			enabled: false,
			market: "indonesia",
			// Consumer marketplace basket — the same buyer as shopee.
			audience: "candidate",
		},
		traveloka: {
			// Traveloka ID (Accesstrade, up to 12.61%). Travel — interview/relocation
			// trips. Lower value (thinner intent). Serves nothing until
			// AFFILIATE_URL_TRAVELOKA is set.
			// 2026-08-04: disabled by code default (PR-2) — zero clicks over 90d
			// telemetry. Re-enable via the PostHog payload once a tracking URL lands.
			payoutModel: "cps",
			declaredValue: 0.12,
			enabled: false,
			market: "indonesia",
			// Interview/relocation trips — booked by the person taking the job.
			audience: "candidate",
		},
		adsense: {
			payoutModel: "ecpm",
			declaredValue: 0.5, // per 1000 impressions (low ID eCPM)
			enabled: false, // off until env wired (Phase 2)
			market: "all",
			// Deliberately left undeclared (= "both"): a display network sells
			// impressions, not an offer, so it has no audience of its own.
		},
		// ── Drop-in link partners ────────────────────────────────────────────────
		// Single-link affiliates registered via `src/lib/affiliate/link-partners.ts`
		// (provider built automatically — no buildOffer needed). When you add an
		// approved partner row + its AFFILIATE_URL_<NAME> secret, ALSO add its
		// payout arm here so the ranker scores it, e.g.:
		//   jobscan: { payoutModel: "cps", declaredValue: 0.3, enabled: true, market: "all", audience: "candidate" },
		// Declare `audience` unless the offer genuinely converts BOTH sides — an
		// arm that omits it competes on the employer surfaces too.
		// Keep declaredValue below deel/educative until real EPC lands (adding an
		// enabled all-market arm shifts Thompson-sampling order — see pick-offer tests).
	},
	// 2026-07-04 affiliate_cap loosening: PostHog telemetry (affiliate_impression
	// vs monetized_suppressed, 30d) showed the shared per-session maxAffiliate
	// budget killing 37-48% of eligible job_sidebar/feed_inline slots on the
	// site's two highest-traffic pages, purely because maxAffiliate was 1 for
	// every non-Pro segment except free_deep (2) — whichever surface resolved
	// first per session spent the ENTIRE budget, starving every other surface
	// for the rest of that session. Bumped each segment by +1 (maxAffiliate)
	// and +1 (sessionImpressionCap, so the extra affiliate slot isn't just
	// borrowed from the total-impression ceiling) — still well under Coalition
	// for Better Ads density norms (max 3 affiliate units/session even for the
	// most-engaged free_deep segment). See job_detail_after_apply's dedicated
	// reserved budget below (RESERVED_AFFILIATE_SURFACES) for the surface that
	// was 100% starved even by this shared pool (it resolves late in the page,
	// after job_sidebar already spent the session's one shared slot).
	aggression: {
		// `maxDisplay` was 0 here until 2026-09-04, which meant an anonymous
		// visitor — most of the site's traffic — could never be shown a
		// third-party display unit. That was invisible for as long as nothing
		// consumed `maxDisplay`; once #4828 routed Adsterra through the guardrail
		// it became "the display network serves nobody". Anonymous readers now get
		// one display unit, the same as `free_shallow`; only Pro is ad-free.
		// 1 display + 2 affiliate exactly saturates `sessionImpressionCap`, so this
		// does not take a slot away from the first-party affiliate cards.
		anon_shallow: {
			maxDisplay: 1,
			maxAffiliate: 2,
			allowInterstitial: false,
			upsellMode: "none",
			sessionImpressionCap: 3,
		},
		anon_deep: {
			maxDisplay: 1,
			maxAffiliate: 2,
			allowInterstitial: false,
			upsellMode: "soft",
			sessionImpressionCap: 5,
		},
		free_shallow: {
			maxDisplay: 1,
			maxAffiliate: 2,
			allowInterstitial: false,
			upsellMode: "soft",
			sessionImpressionCap: 5,
		},
		free_deep: {
			maxDisplay: 2,
			maxAffiliate: 3,
			allowInterstitial: true,
			upsellMode: "remove-ads",
			sessionImpressionCap: 9,
		},
		// Pro is ad-free: `useAdGuardrails` returns PRO_BLOCKED before any cap is
		// read. Zero caps here are defence in depth, so a Pro viewer can never be
		// served a unit even if that early return is ever bypassed.
		pro: {
			maxDisplay: 0,
			maxAffiliate: 0,
			allowInterstitial: false,
			upsellMode: "none",
			sessionImpressionCap: 0,
		},
	},
	surfaces: {
		// Employer-zone surfaces (apps/employers). `audience: "employer"` is what
		// lets the deel arm serve here and ONLY here — every other surface
		// defaults to "candidate". See ARM_AUDIENCES above for why this is not a
		// market split.
		employer_post: { enabled: true, market: "all", audience: "employer" },
		employer_dashboard: {
			enabled: true,
			market: "all",
			audience: "employer",
		},
		job_sidebar: { enabled: true, market: "all" },
		job_detail_after_apply: { enabled: false, market: "all" },
		feed_inline: { enabled: false, market: "all" },
		// PR C (2026-09-28) `feed-inline-display` AdSlot's payload-tunable row.
		// Zero-config (no `positionIndex`) reproduces the WR row-12 default via
		// FEED_INLINE_DISPLAY_DEFAULT_POSITION_INDEX (anchors.ts), same pattern
		// as `feed_inline` above. `enabled` is unused by AdSlot (its kill path is
		// `adsense.disabledSlots`); registered so a payload override of
		// `positionIndex` here validates instead of being rejected.
		feed_inline_display: { enabled: true, market: "all" },
		mobile_sticky: { enabled: false, market: "all" },
		post_apply: { enabled: false, market: "all" },
		blog_top: { enabled: true, market: "all" },
		blog_bottom: { enabled: true, market: "all" },
		category_footer: { enabled: true, market: "all" },
		category_sidebar: { enabled: true, market: "all" },
		search_empty: { enabled: false, market: "all" },
		// Sponsored card under the Hunt results list. Registered enabled so the
		// unknown-surface gate does not kill it; the `hunt-native-ad` PostHog
		// flag is the sole on/off switch for rendering it at all.
		hunt_results: { enabled: true, market: "all" },
		// H7 ad wall (scripts/research/monetization-experiments-2026-10/
		// DECISIONS.md). Registered enabled so the unknown-surface gate does not
		// return `offer: null`; the `ad-wall-placement` PostHog flag is the sole
		// switch for showing the wall at all.
		ad_wall: { enabled: true, market: "all" },
		// Registered from live prod telemetry (30d affiliate_impression) so the
		// unknown-surface gate (resolveOffer/pickOffer) does not silently kill them.
		// enabled:true preserves their current serving. Dynamic `base:variant`
		// surfaces gate on the base (blog_educative:<slug> → blog_educative).
		category_page: { enabled: true, market: "all" },
		apply_page: { enabled: true, market: "all" },
		blog_post: { enabled: true, market: "all" },
		blog_educative: { enabled: true, market: "all" },
		blog_deel_guide: { enabled: true, market: "all" },
		collaboration_post: { enabled: true, market: "all" },
		salary_guide: { enabled: true, market: "all" },
		faq_q2: { enabled: true, market: "all" },
		resume_example_sidebar: { enabled: true, market: "all" },
		cover_letter_sidebar: { enabled: true, market: "all" },
		career_guide_sidebar: { enabled: true, market: "all" },
		interview_questions_sidebar: { enabled: true, market: "all" },
		// PR C+ (Wave 4, 2026-09-28): shipped DARK. Flip to `enabled: true` in the
		// `monetization-settings` payload only after this PR's `LIVE_OK` — see the
		// PR body's post-merge payload step. Blog reads 5.8% ad coverage today
		// (dead config, no NativeAd mount); dashboard 42%.
		blog_index: { enabled: false, market: "all" },
		dashboard_list: { enabled: false, market: "all" },
	},
	// 2026-07-04: job_detail_after_apply was 100% suppressed in the last 7d of
	// telemetry (0 impressions ever landed) — it resolves after job_sidebar on
	// the same job-detail render, so it always lost the shared per-session
	// maxAffiliate slot. Reserved 1 impression/session so the highest-intent
	// moment (right after the viewer applies) always gets its shot. Tune live
	// via PostHog — see the schema doc above.
	reservedAffiliateSurfaces: {
		job_detail_after_apply: 1,
		// The wall shows at most once per session; its one unit must not be
		// starved by a sidebar that spent the shared pool first.
		ad_wall: 1,
	},
	// Nothing withheld: AdSense eligible on every slot and market. Tune live via
	// the PostHog payload — see the schema doc above.
	adsense: {
		disabledSlots: [],
		disabledMarkets: [],
	},
	guardrails: {
		noAboveFoldOnJob: true,
		oneUnitPerViewport: true,
		dismissNegativeWeight: 5,
		clsBudget: 0.1,
		adDensityCap: 0.3, // Coalition for Better Ads
	},
	privacy: {
		consentRequired: true,
		personalizedAdsAllowed: false,
	},
	attribution: {
		windowDays: 30,
		model: "last-click",
		dedup: true,
	},
	partnerClicks: {
		// ON by default — the ONLY safe-on switch in this file. Turning it off
		// re-opens the no-JS bounce loop; the full trade is documented on the
		// schema field above. Read that before flipping it.
		noJsInterstitialEnabled: true,
		// ENABLED = feed on. The code floor is ON so an unreachable PostHog can
		// never silently kill a live commercial rail; flipping it false in the
		// `monetization-settings` payload is what turns the Jobg8 feed off.
		jobg8FeedEnabled: true,
	},
	// Owner rule 2026-09-27: every seeker email carries the sponsor except the
	// MagicLink auth email and employer/B2B mail. `magic_link` never actually
	// reaches this list today (Better Auth renders it via a separate function,
	// `renderMagicLinkEmail`, outside the `notificationEvents` dispatcher in
	// `src/lib/email/render.tsx`) but stays listed defensively. `job_submission`
	// is the admin-only "a job was submitted for review" notice — internal
	// operator mail, not a seeker email. `recruiter_nurture` is the recruiter
	// lifecycle sequence (`src/lib/cron/jobs/lifecycle-recruiter-nurture.tsx`) —
	// also structurally outside this dispatcher today, listed for when/if it
	// is ever wired through it. The Pro exclusion itself is NOT here — it is a
	// hardcoded, non-payload check (`getActiveProUserIds`) so a payload typo
	// can never leak the sponsor to a paying user.
	emailSponsor: {
		excludedTypes: ["magic_link", "job_submission", "recruiter_nurture"],
		// Card-vs-line A/B: 0 = card dark. Raise via the payload, no deploy.
		cardPct: 0,
	},
};
